import React, { useState } from 'react';
import {
  Box,
  Button,
  Slider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Typography,
  Chip,
  Rating,
  IconButton,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import { InteractiveComponent, QuickRepliesData, ScaleData, ListData, MultiSelectData } from '@/types/chat';
import { useSettings } from '@/context/SettingsContext';

interface InteractiveComponentsProps {
  component: InteractiveComponent;
  onResponse: (value: any) => void;
}

const InteractiveComponents: React.FC<InteractiveComponentsProps> = ({ component, onResponse }) => {
  const { language } = useSettings();
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const [multiSelectValues, setMultiSelectValues] = useState<string[]>([]);

  const t = {
    en: {
      submit: 'Submit',
      minSelections: 'Minimum selections:',
      maxSelections: 'Maximum selections:',
      selected: 'selected',
      star: 'star',
      stars: 'stars',
    },
    ar: {
      submit: 'إرسال',
      minSelections: 'الحد الأدنى للاختيارات:',
      maxSelections: 'الحد الأقصى للاختيارات:',
      selected: 'محدد',
      star: 'نجمة',
      stars: 'نجمات',
    },
  }[language];

  const handleResponse = (value: any) => {
    setSelectedValue(value);
    onResponse(value);
  };

  const handleMultiSelectToggle = (value: string) => {
    const newValues = multiSelectValues.includes(value)
      ? multiSelectValues.filter((v) => v !== value)
      : [...multiSelectValues, value];

    setMultiSelectValues(newValues);
  };

  const handleMultiSelectSubmit = () => {
    onResponse(multiSelectValues);
  };

  // Wrapper for all components to ensure proper sizing
  const ComponentWrapper = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      px: { xs: 0, sm: 1 }
    }}>
      {children}
    </Box>
  );

  switch (component.type) {
    case 'quick_replies':
      const quickRepliesData = component.data as QuickRepliesData;

      // Extract min and max labels from options (for NPS-style questions)
      const firstOption = quickRepliesData.options[0];
      const lastOption = quickRepliesData.options[quickRepliesData.options.length - 1];

      // Check if this looks like NPS (first option starts with "0" or contains "Not at all", last contains "10" or "Extremely")
      const isNPSStyle =
        (firstOption.value.startsWith('0') || firstOption.value.includes('Not at all') || firstOption.value.includes('غير محتمل')) &&
        (lastOption.value.includes('10') || lastOption.value.includes('Extremely') || lastOption.value.includes('من المرجح'));

      // Extract labels (remove number prefix like "0 - " or "10 - ")
      const minLabel = firstOption.value.replace(/^\d+\s*-\s*/, '');
      const maxLabel = lastOption.value.replace(/^\d+\s*-\s*/, '');

      return (
        <Box sx={{ mt: 2 }}>
          {isNPSStyle && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
              px: { xs: 0.5, sm: 0 }
            }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '45%'
                }}
              >
                {minLabel}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '45%',
                  textAlign: 'right'
                }}
              >
                {maxLabel}
              </Typography>
            </Box>
          )}
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 0.5, sm: 1 },
            px: { xs: 0.5, sm: 0 }
          }}>
            {quickRepliesData.options.map((option) => (
              <Button
                key={option.id}
                variant={selectedValue === option.value ? 'contained' : 'outlined'}
                onClick={() => handleResponse(option.value)}
                disabled={selectedValue !== null}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  minWidth: { xs: 'auto', sm: '64px' }
                }}
              >
                {option.label}
              </Button>
            ))}
          </Box>
        </Box>
      );

    case 'scale':
      const scaleData = component.data as ScaleData;
      const [scaleValue, setScaleValue] = useState<number>(
        Math.floor((scaleData.max + scaleData.min) / 2)
      );

      return (
        <ComponentWrapper>
          <Box sx={{ mt: 2, width: '100%' }}>
            <Box sx={{ px: { xs: 1, sm: 2 }, mb: 3, maxWidth: '100%', pt: 3 }}>
              <Slider
                value={scaleValue}
                onChange={(_, value) => setScaleValue(value as number)}
                min={scaleData.min}
                max={scaleData.max}
                marks
                step={1}
                valueLabelDisplay="on"
                disabled={selectedValue !== null}
                sx={{
                  width: '100%',
                  '& .MuiSlider-markLabel': {
                    fontSize: { xs: '0.6rem', sm: '0.75rem' }
                  },
                  '& .MuiSlider-valueLabel': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1,
                gap: 1,
                maxWidth: '100%'
              }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.55rem', sm: '0.65rem' },
                    whiteSpace: 'nowrap',
                    flex: '0 1 auto',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '48%'
                  }}
                >
                  {scaleData.minLabel}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.55rem', sm: '0.65rem' },
                    whiteSpace: 'nowrap',
                    flex: '0 1 auto',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '48%',
                    textAlign: 'right'
                  }}
                >
                  {scaleData.maxLabel}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={() => handleResponse(scaleValue)}
              disabled={selectedValue !== null}
              sx={{ mt: 1 }}
              fullWidth
            >
              {t.submit}
            </Button>
          </Box>
        </ComponentWrapper>
      );

    case 'list':
      const listData = component.data as ListData;
      return (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          {listData.items.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => handleResponse(item.value)}
                disabled={selectedValue !== null}
                selected={selectedValue === item.value}
              >
                <ListItemText
                  primary={item.title}
                  secondary={item.description}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      );

    case 'multi_select':
      const multiSelectData = component.data as MultiSelectData;
      return (
        <Box>
          <List>
            {multiSelectData.options.map((option) => (
              <ListItem key={option.id} disablePadding>
                <ListItemButton
                  onClick={() => handleMultiSelectToggle(option.value)}
                  disabled={selectedValue !== null}
                >
                  <Checkbox
                    edge="start"
                    checked={multiSelectValues.includes(option.value)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText primary={option.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {multiSelectData.minSelect && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t.minSelections} {multiSelectData.minSelect}
            </Typography>
          )}
          {multiSelectData.maxSelect && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t.maxSelections} {multiSelectData.maxSelect}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleMultiSelectSubmit}
            disabled={
              selectedValue !== null ||
              !!(multiSelectData.minSelect && multiSelectValues.length < multiSelectData.minSelect) ||
              !!(multiSelectData.maxSelect && multiSelectValues.length > multiSelectData.maxSelect)
            }
            sx={{ mt: 2 }}
            fullWidth
          >
            {t.submit} ({multiSelectValues.length} {t.selected})
          </Button>
        </Box>
      );

    case 'stars':
      const starsData = component.data as ScaleData;
      const [starsValue, setStarsValue] = useState<number>(0);

      return (
        <ComponentWrapper>
          <Box sx={{ mt: 2, textAlign: 'center', width: '100%' }}>
            <Rating
              name="star-rating"
              value={starsValue}
              onChange={(_, newValue) => {
                if (newValue !== null) {
                  setStarsValue(newValue);
                }
              }}
              max={starsData.max}
              size="large"
              icon={<StarIcon fontSize="inherit" />}
              emptyIcon={<StarIcon fontSize="inherit" />}
              sx={{
                fontSize: { xs: '1.75rem', sm: '2.5rem' },
                '& .MuiRating-iconEmpty': {
                  color: 'rgba(0, 0, 0, 0.26)',
                },
                '& .MuiRating-icon': {
                  marginRight: { xs: '2px', sm: '4px' }
                }
              }}
              disabled={selectedValue !== null}
            />
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              px: { xs: 1, sm: 2 },
              gap: 1,
              maxWidth: '100%'
            }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '48%'
                }}
              >
                {starsData.minLabel}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '48%',
                  textAlign: 'right'
                }}
              >
                {starsData.maxLabel}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => handleResponse(starsValue)}
              disabled={selectedValue !== null || starsValue === 0}
              sx={{ mt: 2 }}
              fullWidth
            >
              {t.submit} ({starsValue} {starsValue === 1 ? t.star : t.stars})
            </Button>
          </Box>
        </ComponentWrapper>
      );

    case 'smileys':
      const smileysData = component.data as ScaleData;
      const [smileyValue, setSmileyValue] = useState<number>(0);

      // Map of smiley icons (1-5) with responsive sizing
      const smileyIcons: { [key: number]: React.ReactElement } = {
        1: <SentimentVeryDissatisfiedIcon sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }} />,
        2: <SentimentDissatisfiedIcon sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }} />,
        3: <SentimentNeutralIcon sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }} />,
        4: <SentimentSatisfiedIcon sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }} />,
        5: <SentimentVerySatisfiedIcon sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }} />,
      };

      return (
        <ComponentWrapper>
          <Box sx={{ mt: 2, textAlign: 'center', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 0.25, sm: 1.5 },
                my: 3,
                flexWrap: 'nowrap',
                maxWidth: '100%',
                overflow: 'hidden'
              }}
            >
              {Array.from({ length: smileysData.max }, (_, i) => i + 1).map((value) => (
                <IconButton
                  key={value}
                  onClick={() => setSmileyValue(value)}
                  disabled={selectedValue !== null}
                  sx={{
                    border: smileyValue === value ? '2px solid' : '1px solid transparent',
                    borderColor: smileyValue === value ? 'primary.main' : 'transparent',
                    borderRadius: '50%',
                    padding: { xs: 0.2, sm: 0.5 },
                    minWidth: 0,
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      backgroundColor: 'action.hover',
                    },
                    color:
                      value === 1
                        ? 'error.main'
                        : value === 2
                        ? 'warning.dark'
                        : value === 3
                        ? 'grey.500'
                        : value === 4
                        ? 'success.light'
                        : 'success.main',
                  }}
                >
                  {smileyIcons[value]}
                </IconButton>
              ))}
            </Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
              px: { xs: 1, sm: 2 },
              gap: 1,
              maxWidth: '100%'
            }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '48%'
                }}
              >
                {smileysData.minLabel}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '48%',
                  textAlign: 'right'
                }}
              >
                {smileysData.maxLabel}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => handleResponse(smileyValue)}
              disabled={selectedValue !== null || smileyValue === 0}
              sx={{ mt: 2 }}
              fullWidth
            >
              {t.submit}
            </Button>
          </Box>
        </ComponentWrapper>
      );

    default:
      return null;
  }
};

export default InteractiveComponents;
